// Leaf - Custom Event - Promotion Attribution (shared: select_promotion, view_item_list, view_item, add_to_cart, begin_checkout, purchase) - Start
//
// Shared sessionStorage-backed registry used to carry promotion_id/promotion_name/
// creative_slot from a select_promotion click through to view_item_list, view_item,
// add_to_cart, begin_checkout, and purchase - all fired natively by Leaf Signal via
// Shopify's Web Pixels API, not by this theme. This mechanism only maintains the
// sessionStorage data Signal's pixel is expected to read to enrich those events.
//
// Flow:
//   1. select_promotion click -> registers a pending promotion (by promotion_id)
//   2. Landing on that promotion's collection page -> fills in the promotion's
//      product_ids (read from a JSON blob the collection template renders).
//      Signal is expected to read leaf_promo_registry directly (keyed by
//      collection handle) to attribute view_item_list/collection_viewed for
//      that page - no separate map needed for that event.
//   3. Viewing a product page -> checks that product's id against all pending
//      promotions' product_ids; on match, records it in leaf_product_attribution
//      (sessionStorage, keyed by product_id) for Signal to read when firing
//      view_item/product_viewed.
//   4. add_to_cart -> same check/record into leaf_product_attribution, keyed by
//      product_id, for Signal to read when firing add_to_cart/begin_checkout/
//      purchase (checkout line items carry product_id too, not just variant_id).
//
window.LeafCustomEvents = (function () {
  "use strict";

  var PROMO_REGISTRY_KEY = "leaf_promo_registry";
  var PRODUCT_ATTRIBUTION_KEY = "leaf_product_attribution";

  function readJSON(storage, key, fallback) {
    try {
      var raw = storage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function writeJSON(storage, key, value) {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (e) {
      // sessionStorage unavailable (private mode, quota, etc.) - fail silently,
      // attribution is best-effort and should never block browsing/cart/checkout.
    }
  }

  function getPromotionRegistry() {
    return readJSON(window.sessionStorage, PROMO_REGISTRY_KEY, {});
  }

  function savePromotionRegistry(registry) {
    writeJSON(window.sessionStorage, PROMO_REGISTRY_KEY, registry);
  }

  function getProductAttributionMap() {
    return readJSON(window.sessionStorage, PRODUCT_ATTRIBUTION_KEY, {});
  }

  function saveProductAttributionMap(map) {
    writeJSON(window.sessionStorage, PRODUCT_ATTRIBUTION_KEY, map);
  }

  // Records/refreshes a clicked promotion. Does not clear an already-known
  // product_ids list on re-click of the same promotion within a session.
  function recordPromotionClick(promotionId, promotionName, creativeSlot) {
    if (!promotionId) return;
    var registry = getPromotionRegistry();
    var existing = registry[promotionId];
    registry[promotionId] = {
      promotion_id: promotionId,
      promotion_name: promotionName,
      creative_slot: creativeSlot,
      product_ids: existing && existing.product_ids ? existing.product_ids : null,
      clicked_at: Date.now(),
    };
    savePromotionRegistry(registry);
  }

  // Reads the collection-page product ID blob (see sections/main-collection.liquid)
  // and fills in product_ids for any pending registry entry whose promotion_id
  // matches this collection's handle. Signal is expected to read
  // leaf_promo_registry[<this collection's handle>] directly to attribute
  // view_item_list for this same page - no extra write needed for that.
  function hydrateCollectionProducts() {
    var el = document.querySelector("[data-leaf-collection-products]");
    if (!el) return;

    var data;
    try {
      data = JSON.parse(el.textContent);
    } catch (e) {
      return;
    }
    if (!data || !data.handle || !data.product_ids) return;

    var registry = getPromotionRegistry();
    var entry = registry[data.handle];
    if (entry && !entry.product_ids) {
      entry.product_ids = data.product_ids;
      savePromotionRegistry(registry);
    }
  }

  // Finds the most-recently-clicked active promotion whose product_ids include
  // this product_id. Assumption: if a product belongs to more than one clicked
  // promotion's collection this session, the most recent click wins.
  function findMatchingPromotion(productId) {
    var registry = getPromotionRegistry();
    var match = null;

    Object.keys(registry).forEach(function (key) {
      var entry = registry[key];
      if (!entry.product_ids || entry.product_ids.indexOf(productId) === -1) return;
      if (!match || entry.clicked_at > match.clicked_at) {
        match = entry;
      }
    });

    return match;
  }

  // Matches a single product_id against the registry and writes/clears its
  // entry in leaf_product_attribution accordingly. Shared by add_to_cart and
  // view_item - both are just "does this product_id match an active
  // promotion right now," re-evaluated every time either fires.
  function recordProductAttribution(productId) {
    if (productId === undefined || productId === null) return;

    var attributionMap = getProductAttributionMap();
    var key = String(productId);
    var match = findMatchingPromotion(productId);

    if (match) {
      attributionMap[key] = {
        promotion_id: match.promotion_id,
        promotion_name: match.promotion_name,
        creative_slot: match.creative_slot,
      };
    } else {
      delete attributionMap[key];
    }

    saveProductAttributionMap(attributionMap);
  }

  document.addEventListener("DOMContentLoaded", hydrateCollectionProducts);
  if (document.readyState !== "loading") hydrateCollectionProducts();

  return {
    recordPromotionClick: recordPromotionClick,
    getProductAttributionMap: getProductAttributionMap,
    saveProductAttributionMap: saveProductAttributionMap,
    findMatchingPromotion: findMatchingPromotion,
    recordProductAttribution: recordProductAttribution,
  };
})();
// Leaf - Custom Event - Promotion Attribution (shared: select_promotion, view_item_list, view_item, add_to_cart, begin_checkout, purchase) - End

// Leaf - Custom Event - Custom Event Publish Helper (shared: select_promotion, select_item, apply_sort, select_blog, apply_filter, remove_filter, select_video, start_video, complete_video, open_book, collection_scroll) - Start
//
// Shared helper that broadcasts a custom event (one with no native Shopify
// Web Pixels equivalent) via Shopify's theme-to-pixel bridge, so Leaf Signal
// can capture it by subscribing once to the single topic below, instead of
// pushing to window.dataLayer. All events sent this way are wrapped in one
// envelope - {event_name, params} - so Signal only needs one
// analytics.subscribe("leaf_custom_events", ...) to receive any number of
// distinct custom events, keyed by event_name.
(function () {
  "use strict";

  function publish(eventName, params) {
    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === "function"
      ) {
        window.Shopify.analytics.publish("leaf_custom_events", {
          event_name: eventName,
          params: params || {},
        });
      }
    } catch (e) {
      // best-effort - never block the calling UI flow (form submit, click, etc.)
    }
  }

  window.LeafCustomEvents.publish = publish;
})();
// Leaf - Custom Event - Custom Event Publish Helper (shared: select_promotion, select_item, apply_sort, select_blog, apply_filter, remove_filter, select_video, start_video, complete_video, open_book, collection_scroll) - End

// Leaf - Custom Event - CTA context bridge (view_item, add_to_cart) - Start
//
// view_item and add_to_cart are fired natively by Leaf Signal through Shopify's Web
// Pixels API. That pixel runs in a sandboxed iframe with no access to the DOM, so it
// cannot see which call to action the theme rendered for a product - and the CTA is
// exactly what those reports need in order to express a conversion rate per CTA type.
//
// The theme therefore records the CTA against the product id in sessionStorage, which
// Signal reads back through leafconnect_browser.sessionStorage (the sandbox's proxy for
// the top frame's storage). This is the same bridge the promotion registry already uses.
//
// Two kinds of entry are written:
//   - on a PDP, for the product in view, with cta_location "detail"
//   - on a product card CTA, for that card's product, with cta_location "list"
//
// Entries are keyed by product id and Signal only ever reads the key matching the event
// it is handling, so a stale entry cannot be applied to the wrong product.
(function () {
  "use strict";

  var CTA_CONTEXT_KEY = "leaf_cta_ctx";
  // Keeps the map from growing for the life of the session on a long browse.
  var MAX_ENTRIES = 50;

  function readContextMap() {
    try {
      var raw = window.sessionStorage.getItem(CTA_CONTEXT_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function recordCtaContext(productId, ctaType, ctaLocation, productType) {
    if (!productId || !ctaType) return;

    try {
      var map = readContextMap();
      map[String(productId)] = {
        cta_type: ctaType,
        cta_location: ctaLocation || "detail",
        // Shopify leaves product.type undefined on the add_to_cart pixel payload, so
        // item_category has to reach Signal through this bridge instead.
        product_type: productType || "",
        recorded_at: Date.now()
      };

      var keys = Object.keys(map);
      if (keys.length > MAX_ENTRIES) {
        keys
          .sort(function (a, b) {
            return (map[a].recorded_at || 0) - (map[b].recorded_at || 0);
          })
          .slice(0, keys.length - MAX_ENTRIES)
          .forEach(function (key) {
            delete map[key];
          });
      }

      window.sessionStorage.setItem(CTA_CONTEXT_KEY, JSON.stringify(map));
    } catch (e) {
      // sessionStorage unavailable (private mode, quota). The events still fire,
      // just without CTA attribution - never block the page on this.
    }
  }

  // The PDP's own CTA, rendered server-side into [data-leaf-cta] by
  // snippets/product-buttons.liquid. Read from the attribute rather than from the
  // buttons themselves: the theme renders both "add to cart" and "notify me" into the
  // DOM and hides one, and the fixed bar renders a second copy of whichever is active,
  // so inspecting buttons would be ambiguous.
  function recordPdpCta() {
    var el = document.querySelector("[data-leaf-cta][data-leaf-product-id]");
    if (!el) return false;
    recordCtaContext(
      el.getAttribute("data-leaf-product-id"),
      el.getAttribute("data-leaf-cta"),
      "detail",
      el.getAttribute("data-leaf-product-type")
    );
    return true;
  }

  // Try immediately, and again as the document finishes parsing. This script can run
  // before the product buttons have been parsed, in which case the first attempt finds
  // nothing - and Signal reads the stored value when product_viewed fires, which is
  // later still, so a retry is enough. Listeners are once-only and recording is
  // idempotent, so a successful first attempt costs nothing.
  function initPdpCta() {
    if (recordPdpCta()) return;
    document.addEventListener("DOMContentLoaded", recordPdpCta, { once: true });
    window.addEventListener("load", recordPdpCta, { once: true });
  }

  // A product card's CTA, so an add to cart from a listing is distinguishable from one
  // on the product's own page.
  function handleCardCtaClick(event) {
    var trigger = event.target.closest("[data-leaf-card-cta][data-leaf-product-id]");
    if (!trigger) return;
    recordCtaContext(
      trigger.getAttribute("data-leaf-product-id"),
      trigger.getAttribute("data-leaf-card-cta"),
      "list",
      trigger.getAttribute("data-leaf-product-type")
    );
  }

  document.addEventListener("click", handleCardCtaClick, true);

  initPdpCta();

  window.LeafCustomEvents.recordCtaContext = recordCtaContext;
  window.LeafCustomEvents.recordPdpCta = recordPdpCta;
})();
// Leaf - Custom Event - CTA context bridge (view_item, add_to_cart) - End

// Leaf - Custom Event - view_item on variant change - Start
//
// The new PDP swaps the buttons server-side when the variant changes, without a page
// reload, and the call to action can differ per variant (e.g. an in-stock strap shows
// "add to cart" while a sold-out one shows "notify me"). Shopify's product_viewed only
// fires once per page load, so without this a visitor who switches variant and then
// converts would have their view counted against the CTA they arrived on rather than
// the one they acted on, putting it in the wrong denominator.
//
// Each variant state is a genuine view of a different CTA, so one view_item per variant
// is sent. Note this carries no item_list_id/item_list_name: those are derived by Signal
// from the referrer and describe how the visitor reached the PDP, which has not changed
// when they switch variant.
(function () {
  "use strict";

  var lastVariantId = null;

  function toNumber(value) {
    var num = parseFloat(value);
    return isNaN(num) ? undefined : num;
  }

  function publishVariantViewItem(variant) {
    var product = (window.__teddy || {}).viewedProduct;
    if (!product || !variant) return;

    var ctaEl = document.querySelector("[data-leaf-cta][data-leaf-product-id]");
    var ctaType = ctaEl ? ctaEl.getAttribute("data-leaf-cta") : "";
    var currency = (window.__teddy || {}).currency || "USD";
    // Shopify serialises variant prices in cents.
    var price = toNumber(variant.price);
    if (price !== undefined) price = price / 100;

    window.LeafCustomEvents.publish("view_item", {
      currency: currency,
      value: price,
      affiliation: "Online Store",
      product_id: product.id,
      product_name: product.title,
      cta_type: ctaType,
      cta_location: "detail",
      items: [
        {
          item_id: variant.sku || product.id,
          item_shopify_id: product.id,
          item_shopify_variant: variant.id,
          item_name: product.title,
          item_variant: variant.title || "Default Title",
          item_category: product.type || "",
          item_brand: product.vendor || "",
          currency: currency,
          price: price,
          quantity: 1,
          image_url: product.imageUrl || "",
          product_url: window.location.href,
          // Drives Signal's promotion lookup, then stripped before the event goes out -
          // same contract as select_item.
          product_id: product.id
        }
      ]
    });
  }

  document.addEventListener("pdp:refreshed", function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || variant.id === lastVariantId) return;
    lastVariantId = variant.id;

    // The buttons were just re-rendered server-side, so [data-leaf-cta] now holds this
    // variant's call to action. Refresh the stored context before anything reads it.
    if (window.LeafCustomEvents.recordPdpCta) window.LeafCustomEvents.recordPdpCta();

    publishVariantViewItem(variant);
  });
})();
// Leaf - Custom Event - view_item on variant change - End

// Leaf - Custom Event - generate_lead - Start
//
// generate_lead uses its own dedicated topic and shape, per Leaf Signal's
// own lead-tracking schema - NOT the shared {event_name, params} envelope
// every other custom event in this project uses (window.LeafCustomEvents.
// publish, above). lead_type is a top-level field (maps to GA4's
// ecommerce.lead_type per Signal's mapping); PII lives under metaData with
// $-prefixed keys ($email, $phone, $first_name, $last_name) for Google Ads
// enhanced conversions matching (hashed via gtag user_data on Signal's/
// Google's side, not by this theme). product_name/product_id are included
// as extra top-level fields for lead types that are tied to a product
// (notify_me, contact_to_purchase) - not part of Signal's documented
// shape, kept per explicit request so it's still known which product a
// lead is about, without needing the full Shopify product object (images[],
// variants[]) Signal's schema otherwise supports - that was deliberately
// left out, see generate_lead.md.
//
// Any field with no value (blank/undefined) is omitted from its object
// entirely rather than sent as an empty string - this is a foreign schema
// parsed on Signal's own terms, and "field absent" vs. "field present but
// empty" may not be treated the same downstream (e.g. hashing an empty
// string for enhanced conversions), so only real, present data is sent.
(function () {
  "use strict";

  function publishLead(fields) {
    try {
      if (
        !(
          window.Shopify &&
          window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === "function"
        )
      ) {
        return;
      }

      var payload = { lead_type: fields.leadType };

      // lead_source carries the Klaviyo "signup source - email" value verbatim,
      // so a lead in GA4 can be matched to the same signup in Klaviyo.
      if (fields.leadSource) payload.lead_source = fields.leadSource;
      if (fields.productName) payload.product_name = fields.productName;
      if (fields.productId) payload.product_id = fields.productId;

      // cta_type is the call to action the PDP was showing, and cta_location is
      // whether that was the product's own page ("detail") or a card ("list").
      // Together they let a lead be compared against the view_item events for the
      // same CTA, which is what gives a per-CTA conversion rate.
      if (fields.ctaType) payload.cta_type = fields.ctaType;

      // The caller passes "detail" because these modals live on the product page, but
      // the same modals open from a card on a collection page. The CTA context written
      // when the card button was clicked is the only reliable record of which it was,
      // so prefer it and fall back to what the caller said.
      var ctaLocation = fields.ctaLocation;
      try {
        var ctxRaw = window.sessionStorage.getItem("leaf_cta_ctx");
        if (ctxRaw && fields.productId) {
          var ctxEntry = JSON.parse(ctxRaw)[String(fields.productId)];
          if (ctxEntry && ctxEntry.cta_location) ctaLocation = ctxEntry.cta_location;
        }
      } catch (e) {}
      if (ctaLocation) payload.cta_location = ctaLocation;

      // GA4 has no built-in metric counting lead sub-types, so each lead carries a
      // numeric flag that is registered as a custom metric and summed in reports.
      var COUNT_PARAM_BY_LEAD_TYPE = {
        contact_to_purchase: "contact_to_purchase_count",
        notify_me: "notify_me_count",
        coming_soon: "coming_soon_count"
      };
      var countParam = COUNT_PARAM_BY_LEAD_TYPE[fields.leadType];
      if (countParam) payload[countParam] = 1;

      // Signal falls back to GBP when no product is attached, so send the
      // store currency explicitly on every lead.
      payload.currency = fields.currency || "USD";
      if (fields.value) payload.value = fields.value;

      var metaData = {};
      if (fields.email) metaData.$email = fields.email;
      if (fields.phone) metaData.$phone = fields.phone;
      if (fields.firstName) metaData.$first_name = fields.firstName;
      if (fields.lastName) metaData.$last_name = fields.lastName;
      if (fields.leadSource) metaData.$source = fields.leadSource;
      if (Object.keys(metaData).length > 0) payload.metaData = metaData;

      window.Shopify.analytics.publish("leaf_lead_submitted", payload);
    } catch (e) {
      // best-effort - never block the calling UI flow (form submit, etc.)
    }
  }

  window.LeafCustomEvents.publishLead = publishLead;
})();
// Leaf - Custom Event - generate_lead - End

// Leaf - Custom Event - select_promotion - Start
(function () {
  "use strict";

  function getCollectionHandleFromUrl(url) {
    try {
      var path = new URL(url, window.location.origin).pathname;
      var match = path.match(/\/collections\/([^/?#]+)/);
      return match ? match[1] : "";
    } catch (e) {
      return "";
    }
  }

  function handleClick(event) {
    var trigger = event.target.closest("[data-ga-select-promotion]");
    if (!trigger) return;

    // Leaf - Custom Event - select_promotion - Start
    // Hero banners render inside a Swiper carousel (site-carousel). Swiper
    // cancels the click's default navigation (event.preventDefault()) when
    // it detects the click was actually the end of a drag/swipe, via its
    // own capture-phase listener on the carousel container - which fires
    // after this listener would if this listener used capture phase (a
    // document-level capture listener runs before any descendant's capture
    // listener, so it would always run too early to see Swiper's decision).
    // Listening in the bubble phase instead (see the addEventListener call
    // below) lets Swiper's capture-phase cancellation happen first, so this
    // check reliably skips drag-releases: without it, this would report a
    // promotion click (and record it for attribution) even when Swiper
    // decided nothing was actually navigated to.
    if (event.defaultPrevented) return;
    // Leaf - Custom Event - select_promotion - End

    var href = trigger.getAttribute("href") || "";
    var promotionId = getCollectionHandleFromUrl(href);
    var promotionName = trigger.getAttribute("data-ga-promotion-name") || "";
    var creativeSlot = trigger.getAttribute("data-ga-creative-slot") || "";

    window.LeafCustomEvents.recordPromotionClick(promotionId, promotionName, creativeSlot);

    window.LeafCustomEvents.publish("select_promotion", {
      promotion_id: promotionId,
      promotion_name: promotionName,
      creative_slot: creativeSlot,
    });
  }

  // Leaf - Custom Event - select_promotion - Start
  // Bubble phase (not capture) - see handleClick's comment above for why.
  document.addEventListener("click", handleClick);
  // Leaf - Custom Event - select_promotion - End
})();
// Leaf - Custom Event - select_promotion - End

// Leaf - Custom Event - select_item - Start
//
// Fires when a customer clicks a product card on a collection listing page
// to go to its product page. GA4 standard select_item shape:
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce#select_item-gtm
// The card link only carries the data-ga-select-item marker/attributes when
// rendered from sections/main-collection.liquid (see
// snippets/product-item.liquid) - product-item.liquid is reused on search,
// homepage, and PDP-related-products contexts too, and those callers don't
// pass the list params, so their cards have no attributes and this handler
// naturally never fires for them.
(function () {
  "use strict";

  function toNumber(value) {
    var num = parseFloat(value);
    return isNaN(num) ? undefined : num;
  }

  function handleClick(event) {
    var trigger = event.target.closest("[data-ga-select-item]");
    if (!trigger) return;

    // Leaf - Custom Event - select_item - Start
    // product-item.liquid nests other fully-interactive controls inside this
    // same tracked <a> - a wishlist-star <button> (data-wishlist-toggle) and,
    // on desktop hover, the add-to-cart/notify-me/contact-to-purchase button
    // block (snippets/product-card-buttons.liquid, which includes a <form>
    // with its own submit <button>). This listener runs in the capture phase
    // (see the addEventListener call below), which fires before any of those
    // nested controls' own bubble-phase click handlers (e.g.
    // snippet-wishlist-manager.js's stopPropagation()) get a chance to run -
    // so without this check, clicking "Add to Cart" or the wishlist star
    // would also incorrectly fire select_item, misattributing a wishlist/cart
    // action as "the user clicked through to view this product." Skip
    // whenever the click landed on/inside a nested button, input, or form
    // rather than directly on the card link itself.
    var nestedControl = event.target.closest("button, input, form");
    if (nestedControl && trigger.contains(nestedControl)) return;
    // Leaf - Custom Event - select_item - End

    var itemListId = trigger.getAttribute("data-ga-item-list-id") || "";
    var itemListName = trigger.getAttribute("data-ga-item-list-name") || "";

    // Event-scoped product identity and CTA, so select_item can be compared against
    // view_item and the lead events in a single product-level report. The item-scoped
    // product_id below is stripped by Signal before the event goes out, so these have
    // to be sent separately at event level.
    var selectItemParams = {
      item_list_id: itemListId,
      item_list_name: itemListName,
      currency: trigger.getAttribute("data-ga-currency") || "",
      product_id: trigger.getAttribute("data-ga-product-id") || "",
      product_name: trigger.getAttribute("data-ga-item-name") || "",
      cta_location: "list"
    };

    var cardCtaType = trigger.getAttribute("data-ga-cta-type") || "";
    if (cardCtaType) selectItemParams.cta_type = cardCtaType;

    window.LeafCustomEvents.publish("select_item", {
      ...selectItemParams,
      items: [
        {
          item_id: trigger.getAttribute("data-ga-item-id") || "",
          item_name: trigger.getAttribute("data-ga-item-name") || "",
          item_brand: trigger.getAttribute("data-ga-item-brand") || "",
          item_variant: trigger.getAttribute("data-ga-item-variant") || "",
          item_category: trigger.getAttribute("data-ga-item-category") || "",
          price: toNumber(trigger.getAttribute("data-ga-price")),
          index: toNumber(trigger.getAttribute("data-ga-index")),
          item_list_id: itemListId,
          item_list_name: itemListName,
          quantity: 1,
          // Leaf - Custom Event - select_item - Start
          // product_id (not a standard GA4 item field) lets Leaf Signal
          // correlate this item against leaf_product_attribution
          // (sessionStorage, keyed by product_id) the same way it does for
          // add_to_cart/view_item - item_id above is SKU-first and can't be
          // used for that lookup.
          product_id: trigger.getAttribute("data-ga-product-id") || "",
          // Leaf - Custom Event - select_item - End
        },
      ],
    });
  }

  document.addEventListener("click", handleClick, true);
})();
// Leaf - Custom Event - select_item - End

// Leaf - Custom Event - select_blog - Start
//
// Fires when a customer clicks a blog/article card. No native Shopify Web
// Pixels event covers a blog-card click, so this theme broadcasts it itself
// via window.LeafCustomEvents.publish(...). Not scoped to a single page -
// the marker attributes are rendered on the shared card partials/sections
// (snippets/article-card.liquid, snippets/article-card-v2.liquid,
// sections/exclusive-content-list.liquid) wherever they're used across the
// site, not just the homepage's Latest Content / Explore Latest Films /
// More Content modules where this was first requested.
//
// Redirects to select_video instead when the clicked link points into
// /blogs/videos/ or /blogs/exclusive/ - those are video content, and
// should only ever report as select_video, never both. This check is
// generic (any [data-ga-select-blog] card, based on its own href), not
// limited to article-card-v2.liquid - see select_video.md for why that
// snippet's own blog.handle == 'videos' gate was removed in favor of this
// single, centralized check, and why this also picks up "exclusive" blog
// links, which the old gate never covered.
(function () {
  "use strict";

  function isVideoContentUrl(url) {
    return /\/blogs\/(videos|exclusive)\//.test(url || "");
  }

  function handleClick(event) {
    var trigger = event.target.closest("[data-ga-select-blog]");
    if (!trigger) return;

    // Leaf - Custom Event - select_blog - Start
    // Several of this card's homes (Latest Content, Explore Latest Films)
    // are Swiper carousels, which cancel the click's default navigation
    // when it was actually a drag/swipe release. Listening in the bubble
    // phase (see the addEventListener call below), instead of capture,
    // lets Swiper's own capture-phase cancellation run first, so this
    // check reliably skips drag-releases - see select_promotion's handler
    // above for the full explanation of why phase choice matters here.
    if (event.defaultPrevented) return;
    // Leaf - Custom Event - select_blog - End

    var href = trigger.getAttribute("href") || "";
    var blogName = trigger.getAttribute("data-ga-blog-name") || "";

    if (isVideoContentUrl(href)) {
      window.LeafCustomEvents.publish("select_video", {
        video_title: blogName,
      });
      return;
    }

    window.LeafCustomEvents.publish("select_blog", {
      blog_id: trigger.getAttribute("data-ga-blog-id") || "",
      blog_name: blogName,
    });
  }

  // Leaf - Custom Event - select_blog - Start
  // Bubble phase (not capture) - see handleClick's comment above.
  document.addEventListener("click", handleClick);
  // Leaf - Custom Event - select_blog - End
})();
// Leaf - Custom Event - select_blog - End

// Leaf - Custom Event - select_video - Start
//
// Fires when a customer clicks a video card. No native Shopify Web Pixels
// event covers a video-card click, so this theme broadcasts it itself via
// window.LeafCustomEvents.publish(...). Two sources feed this:
// (1) select_blog's own handler above, which redirects here whenever a
// [data-ga-select-blog] card's href points into /blogs/videos/ or
// /blogs/exclusive/ - this covers snippets/article-card.liquid and
// article-card-v2.liquid generically, without needing per-snippet blog-type
// checks in Liquid; (2) three video-specific card markups inside the
// header's Videos mega-menu in snippets/site-nav-content.liquid ("Films &
// Series" and "New to YouTube"), which carry their own dedicated
// data-ga-select-video marker since they were never wired for select_blog
// at all. Not scoped to a single page.
(function () {
  "use strict";

  function handleClick(event) {
    var trigger = event.target.closest("[data-ga-select-video]");
    if (!trigger) return;

    // Leaf - Custom Event - select_video - Start
    // These mega-menu cards aren't inside a Swiper carousel today, so this
    // guard is a no-op in practice right now - kept for consistency with
    // select_blog/select_promotion (see select_promotion's handler for the
    // full explanation) in case this marker is ever reused somewhere
    // draggable.
    if (event.defaultPrevented) return;
    // Leaf - Custom Event - select_video - End

    window.LeafCustomEvents.publish("select_video", {
      video_title: trigger.getAttribute("data-ga-video-title") || "",
    });
  }

  // Leaf - Custom Event - select_video - Start
  document.addEventListener("click", handleClick);
  // Leaf - Custom Event - select_video - End
})();
// Leaf - Custom Event - select_video - End

// Leaf - Custom Event - open_book - Start
//
// Fires when a customer clicks a "Read It Now" or "Download a PDF" CTA on
// an e-book page (/pages/e-book/<handle>, the metaobject-templated e_book
// page). No native Shopify Web Pixels event covers this, so this theme
// broadcasts it itself via window.LeafCustomEvents.publish(...). Marker
// attributes are rendered on both sections/ebook-hero.liquid and
// sections/ebook-chapters.liquid, which independently render the same two
// CTAs further down the same page - both are tracked identically.
(function () {
  "use strict";

  function handleClick(event) {
    var trigger = event.target.closest("[data-ga-open-book]");
    if (!trigger) return;

    window.LeafCustomEvents.publish("open_book", {
      book_title: trigger.getAttribute("data-ga-book-title") || "",
      cta_text: trigger.getAttribute("data-ga-cta-text") || "",
    });
  }

  document.addEventListener("click", handleClick, true);
})();
// Leaf - Custom Event - open_book - End

// Leaf - Custom Event - view_item (Signal attribution support) - Start
//
// view_item is fired natively by Leaf Signal via Shopify's Web Pixels API
// (product_viewed) - this theme does not push it. Reuses the existing
// window.__teddy.viewedProduct global (see sections/main-product.liquid) to
// get the current product's id, and records its attribution the same way
// add_to_cart does, so Signal can read leaf_product_attribution when it
// fires product_viewed for this page.
(function () {
  "use strict";

  function init() {
    var viewedProduct = window.__teddy && window.__teddy.viewedProduct;
    if (!viewedProduct || viewedProduct.id === undefined || viewedProduct.id === null) return;

    window.LeafCustomEvents.recordProductAttribution(viewedProduct.id);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
// Leaf - Custom Event - view_item (Signal attribution support) - End

// Leaf - Custom Event - add_to_cart (Signal attribution support) - Start
//
// add_to_cart is fired natively by Leaf Signal via Shopify's Web Pixels API
// (product_added_to_cart) - this theme does not push it. This block's only
// job is to keep leaf_product_attribution up to date so Signal's own pixel
// can read it and attach promotion_id/promotion_name/creative_slot to the
// event it fires natively (and, downstream, to begin_checkout/purchase for
// the same product_id).
(function () {
  "use strict";

  // Called from assets/global.js (SiteCart.addVariantToCart) with the "items"
  // array returned by Shopify's /cart/add.js response.
  function recordCartAttribution(items) {
    if (!items || !items.length) return;

    items.forEach(function (item) {
      window.LeafCustomEvents.recordProductAttribution(item.product_id);
    });
  }

  window.LeafCustomEvents.recordCartAttribution = recordCartAttribution;
})();
// Leaf - Custom Event - add_to_cart (Signal attribution support) - End
